import React from "react";
import { Link } from "react-router";
import Utils from "../../../Common/Utils";
import Typography from "Components/DesignSystem/Typography/Typography";
import DeltaTable from "Components/Common/DeltaTable";
import AccordionLayout from "Components/Common/components/Accordion/AccordionLayout";
import AccordionHeader from "Components/Common/components/Accordion/AccordionHeader";
import AccordionBody from "Components/Common/components/Accordion/AccordionBody";
import LiveDraftButton from "Components/GMS/Products/components/LiveDraftButton";
import CustomAttributeTag from 'Components/Common/CustomAttributeTag';

const LocalLink = ({href, value}) => (
  <Link
    to={href}
    className="tw-cursor-pointer"
  >
    {value}
  </Link>
)

// The "Name/Id" columns show the linked name plus an "ID" chip, matching the product listing
// table: hovering the chip reveals the full UID with a copy button (LiveDraftButton type="id"
// renders into the app-wide #tooltip-portal declared in Templates/Layout.tsx).
const NameWithUid = ({href, name, item}) => (
  <span className="tw-inline-flex tw-items-center tw-gap-2 tw-min-w-0">
    <LocalLink href={href} value={name}/>
    {item?.id && <LiveDraftButton item={item} type="id"/>}
  </span>
)

const ProductsTableViewSearch = ({ res }) => {
  function getRules() {
    let entity = res.data.result.filter(
      (result) => result.entity === "CompatioRule"
    );
    if (entity.length > 0) {
      return entity[0].data;
    }
    return [];
  }

  function getCategory() {
    let entity = res.data.result.filter(
      (result) => result.entity === "Category"
    );
    if (entity.length > 0) {
      return entity[0].data;
    }
    return [];
  }

  function getCategoryType() {
    let entity = res.data.result.filter(
      (result) => result.entity === "CategoryType"
    );
    if (entity.length > 0) {
      return entity[0].data;
    }
    return [];
  }

  function geAttributes() {
    let attributeEntity = res.data.result.filter(
      (result) => result.entity === "Attribute"
    );
    if (attributeEntity.length > 0) {
      return attributeEntity[0].data;
    }
    return [];
  }

  function getCategories() {
    let productsEntity = res.data.result.filter(
      (result) => result.entity === "Product"
    );
    if (productsEntity.length > 0) {
      return productsEntity[0].data;
    }
    return [];
  }

  function getProductAttributes(data) {
    let attributes = [];
    data["product"].forEach(function (product) {
      product["attr_vals"].forEach(function (attributeVal) {
        attributes.push(attributeVal["attr_name"]);
      });
    });
    return [...new Set(attributes)];
  }

  /**
   * Custom attribute names in a category group (AB#14859). Search has no server-side column
   * list — the columns are derived from the rows above — so the flag is derived the same way,
   * from `attr_vals[].is_custom`. One product carrying the flag marks the column, since the
   * flag belongs to the Attribute itself and not to any one product.
   */
  function getCustomProductAttributes(data) {
    const custom = new Set();
    data["product"].forEach(function (product) {
      product["attr_vals"]?.forEach(function (attributeVal) {
        if (attributeVal?.is_custom === true) custom.add(attributeVal["attr_name"]);
      });
    });
    return custom;
  }

  function getProducts(data) {
    let products = [];
    data["product"].forEach(function (product) {
      // join attribute values
      const attrs = product.attr_vals?.reduce((acc, attr) => {
        const {attr_name, attr_value} = attr
        return {...acc, [attr_name]: Utils.joinCsvValues(attr_value.map((it) => it.value))}
      }, {})
      products.push({...product, ...attrs});
    });
    return products
  }


  const truncate = (input, stringLength) => {
    if (Utils.isUndefinedOrNull(input)) return "-";
    return input.length > stringLength
      ? `${input.toString().substring(0, stringLength)}...`
      : input;
  };

  const tableOptions = {
    disableSort: true,
    disableHeaderControls: true,
    disableRowOptions: true,
    striped: true,
    footer: false    
  }
  const products =  getCategories().map((categoryData) => {
    return {
      label: "Products - " + categoryData["category"],
      data: getProducts(categoryData),
      columns: [{name: "Name/Id", column: "name", options: {callbackComponent: (item) => <NameWithUid href={`/gms/product/details/${item.id}`} name={truncate(item["name"], 43)} item={item}/>}},
        {name: "Model Number", column: "Model Number"},
        {name: "Product Lifecycle Status", column: "Product Lifecycle Status"},
        ...Utils.generateHeadersFromArray(getProductAttributes(categoryData), getCustomProductAttributes(categoryData))
      ],
      tableOptions: {...tableOptions, pagination: "manual", footer: true}
    }
  })

  const dataTypes = [
    {label: "Category", data: getCategory(), columns: [{name: "Name/Id", column: "name", options: {callbackComponent: (item) => <NameWithUid href={`/gms/categories/c/${item["id"]}`} name={truncate(item["name"], 43)} item={item}/>}}]},
    {label: "Category Type", data: getCategoryType(), columns: [{name: "Name/Id", column: "name", options: {callbackComponent: (item) => <NameWithUid href={`/gms/categories/ct/${item["id"]}`} name={truncate(item["name"], 43)} item={item}/>}}]},
    ...products,
    {label: "Attributes", data: geAttributes(), columns: [{name: "Name", column: "name", options: {
      /* AB#14859 — the Attributes tab lists GMS attributes, the same entity the Attributes
         module tags, so a custom one is tagged here too. The glyph sits outside the link. */
      callbackComponent: (item) => (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          {item["is_custom"] === true &&
            <CustomAttributeTag data-testid={`search-attr-custom-tag-${item["id"]}`} />}
          <LocalLink href={`/gms/attributes/${item["id"]}`} value={truncate(item["name"], 43)}/>
        </span>
      )
      }}, {name: "Value", column: "value"}]},
    {label: "Rules", data: getRules(), columns: [
      {name: "Name", column: "name", options: {
        callbackComponent: (item) => <LocalLink href={`/gms/rule/edit/${truncate(item["uid"])}`} value={truncate(item["name"], 43)}/>
      }}, 
      {name: "Category 1", column: "left_category_name"},
      {name: "Category 2", column: "right_category_name"},
      {name: "Type", column: "rule_type"},
      {name: "Status", column: "rule_status", options: {callbackComponent: (item) => item["rule_status"] !== 0 ? "Active" : "InActive"}},
      {name: "Effective Date", column: "updated_on"}
    ]},
  ]
  return (
      <div>
        {
          dataTypes.map((type, index) => {
            if (!type.data || type.data?.length < 1) return null
            return (
              <AccordionLayout key={type.label}>
              {(provided) => (
              <div className="rounded tw-border-b tw-border-r tw-border-l tw-mb-5">
                  <AccordionHeader {...provided.getHeaderProps()} data-testid={`search-results-accordion-${type.label}`} options={{arrowPosition: "left"}}>
                    {/* DS Typography, ink-toned. Was the legacy Common `Label`, whose
                        `color` defaults to the inline literal "black" — black text on
                        the dark accordion in dark mode. `sub-title-label` (12px/600) is
                        the nearest DS step to its 0.8rem/600. A <span>, not the old
                        <label>: this sits inside the header button and labels no field. */}
                    <span className="tw-px-3">
                      <Typography
                        variant="sub-title-label"
                        as="span"
                        className="tw-text-ink"
                        data-testid={`search-results-accordion-${type.label}-title`}
                      >
                        {`${type.label} (${type.data.length})`}
                      </Typography>
                    </span>
                  </AccordionHeader>
              
                  <AccordionBody {...provided.getBodyProps()}>
                    <DeltaTable
                      columns = {type.columns}
                      data = {type.data}
                      options={type.tableOptions ?? tableOptions}
                      totalRecords={type.data.length}
                      />
                  </AccordionBody> 
                </div>
              )}
              </AccordionLayout>
            )
          })
        }
      </div>
  );
};
export default ProductsTableViewSearch;
